<template>
  <transition-group :css="false" @enter="enter" @afterEnter="afterEnter" @leave="leave" class="tag-wrapper">
    <template v-for="(tag, index) in activeTags" tag="div">
      <tag-rich
        v-if="tag.type === 'rich'"
        :key="`rich-${tag._id}`"
        :data-type="'rich'"
        :data-index="index"
        :data-tag-id="tag._id"
        v-bind="tag"
        :active="tag._id === activeTagId"
        @click.native="setActiveTagId(tag._id)"
      />
      <tag-reveal
        v-if="tag.type === 'reveal'"
        :key="`reveal-${tag._id}`"
        :data-type="'reveal'"
        :data-index="index"
        :data-tag-id="tag._id"
        v-bind="tag"
        :active="tag._id === activeTagId"
        @click.native="setActiveTagId(tag._id)"
      />
      <tag-basic
        v-if="tag.type === 'basic'"
        :data-type="'basic'"
        :data-index="index"
        :key="`basic-${tag._id}`"
        :data-tag-id="tag._id"
        v-bind="tag"
        :active="tag._id === activeTagId"
        @click.native="setActiveTagId(tag._id)"
      />
      <tag-box
        v-if="tag.type === 'box'"
        :data-type="'box'"
        :data-index="index"
        :key="`box-${tag._id}`"
        :data-tag-id="tag._id"
        v-bind="tag"
      />
      <tag-basic-icon
        v-if="tag.type === 'basicIcon'"
        :data-type="'basicIcon'"
        :data-index="index"
        :key="`basicIcon-${tag._id}`"
        :data-tag-id="tag._id"
        v-bind="tag"
      />
      <tag-action
        v-if="tag.type === 'action'"
        :key="`action-${tag._id}`"
        :data-type="'action'"
        :data-index="index"
        :data-tag-id="tag._id"
        v-bind="tag"
        :active="tag._id === activeTagId"
        @click.native="setActiveTagId(tag._id)"
      />
      <tag-reference
        v-if="tag.type === 'reference' && tag.isVisible"
        :key="`reference-${tag._id}`"
        data-type="reference"
        :data-index="index"
        :data-tag-id="tag._id"
        v-bind="tag"
        :active="tag._id === activeTagId"
        @click.native="setActiveTagId(tag._id)"
      />
      <polygon-shape
        v-for="(polygon, polygonIndex) in tag.polygons || []"
        :key="polygon._id || `${tag._id}_polygon_${polygonIndex}`"
        :polygon="polygon"
        @click.native="triggerTagFromPolygon(tag, $event)"
      />
    </template>
  </transition-group>
</template>
<script>
import PolygonShape from '@/components/PolygonShape'
import TagAction from '@/components/TagAction'
import TagBasic from '@/components/TagBasic'
import TagBasicIcon from '@/components/TagBasicIcon'
import TagBox from '@/components/TagBox'
import TagReveal from '@/components/TagReveal'
import TagRich from '@/components/TagRich'
import TagReference from '@/components/TagReference'
import { mapGetters } from 'vuex'
import _ from 'lodash'
import { bus } from '../main'

export default {
  name: 'tags',
  
  components: { PolygonShape, TagAction, TagBasic, TagBox, TagReveal, TagRich, TagBasicIcon, TagReference },

  props: ['tags'],

  data () {
    return {
      activeTags: [],

      activeTagId: null,
      animating: false,
      animationIndex: 0,
      animationSpeed: 100,
      delayIndex: 0,
    }
  },

  watch: {
    $route () {
      this.activeTagId = null
      this.$store.commit('SET_ACTIVE_TAGS_OVERRIDE', null)
    },

    tags (newTags, oldTags) {
      if (!_.isEqual(oldTags, newTags)) {
        this.delayIndex = 0

        if (oldTags.length) {
          this.activeTags = []
        }

        this.setReferenceTagOrder()
        this.$store.commit('addAnimationToQueue', 'Tags')
      }
    }
  },

  computed: {
    ...mapGetters([
      'language'
    ]),
  },

  mounted () {
    bus.$on('animateTags', () => {
      this.activeTags = this.tags

      if (this.activeTags === this.tags) {
        bus.$emit('finishedAnimation', 'Tags')
      }
    })

    bus.$on('navClick', () => {
      this.activeTagId = null
    })
  },

  methods: {
    enter (el, done) {
      const tagType = el.getAttribute('data-type')

      this.animationSpeed = 100

      if (tagType === 'basic') {
        this.animationSpeed = 50
      }

      setTimeout(() => {
        el.classList.add('enter')
        done()
      }, (parseInt(this.delayIndex++) + 1) * this.animationSpeed)
    },

    afterEnter (el) {
      if (parseInt(el.dataset.index) + 1 === this.activeTags.length) {
        bus.$emit('finishedAnimation', 'Tags')
        this.delayIndex = 0
      }       
    },

    leave (el) {
      el.classList.add('leave')
      el.classList.remove('enter')
    },

    deselectActiveSlide () {
      this.activeInteractiveSlideId = null
    },

    resetAnimation () {
      this.delayIndex = 0
      this.tagsToAnimate = []
      this.animationIndex = 0
    },

    setActiveSlide (id) {
      this.activeInteractiveSlideId = id
      this.activeTagId = null
    },

    setSlideTags () {
      this.slideTags = []
      this.$nextTick(() => {
        if (this.activeInteractiveSlideTags) {
          this.slideTags = _.sortBy(this.activeInteractiveSlideTags, tag => { return tag.pos.x })
        } else {
          this.slideTags = []
        }
      })
    },

    setActiveTagId (id) {
      if (this.activeTagId === id) {
        this.activeTagId = null
      } else {
        this.activeTagId = id
      }
    },

    triggerTagFromPolygon (tag, event) {
      if (event) {
        event.stopPropagation()
      }

      const tagRoot = Array.from(this.$el.querySelectorAll('[data-tag-id]'))
        .find(el => el.dataset.tagId === String(tag._id))
      if (!tagRoot) {
        this.setActiveTagId(tag._id)
        return
      }

      let clickTarget = tagRoot

      if (tag.type === 'action') {
        clickTarget = tagRoot.querySelector('.tag-action') || tagRoot
      } else if (tag.type === 'basic') {
        clickTarget = tagRoot.querySelector('.tag-basic') || tagRoot
      } else if (tag.type === 'rich') {
        clickTarget = tagRoot.querySelector('.tag-rich') || tagRoot
      }

      clickTarget.click()
    },

    setReferenceTagOrder () {
      let tags = this.tags.filter(function(tag) {
        return tag.type == 'reference'
      })

      tags = _.orderBy(tags, ['overrideOrder', 'pos.x'], ['asc'])

      tags.forEach((tag, index) => {
        if (!tag.hideTagReference) {
          tag.referenceIndex = index + 1
        }
      })
    }
  }
}
</script>
<style lang="scss" scoped>
.tag-wrapper {
  position: absolute;
  top: 0;
  left: 0;
  z-index: 50;

  .layout-switched & {
    top: 10rem;
  }
}
</style>
